/**
 * Detail page for a single ClientIncentive. Drives every action:
 *   • Sales split modal — exactly 3 rows (BDA / BDE / BDM) bound to the
 *     submission's salesTeam. Live sum + max-allowed indicator.
 *   • Ops split modal — admin adds operations users one-by-one. Each user
 *     must have salesDesignation === 'operations' (filtered via /users/picker).
 *   • Approve & lock / Unlock / Mark paid — admin-only buttons; lifecycle
 *     enforced server-side too.
 *   • Audit log — every state change in reverse chronological order.
 *
 * Anyone whose user appears in salesTeam / salesAllocations / opsAllocations
 * can read this page; only the assigned BDM (or admin) can submit the sales
 * split; only admin can do everything else.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import {
    ArrowLeft, History, Lock as LockIcon, Unlock as UnlockIcon, BadgeCheck,
    IndianRupee, Plus, Trash2, AlertTriangle, FileText, ShieldCheck, RotateCcw,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useAuth } from '../../store/auth';
import { StatusChip, KV, ModalShell } from './IncentiveDealClosures.jsx';

export default function IncentiveDetail() {
    const { id } = useParams();
    const me = useAuth(s => s.user);
    const loc = useLocation();
    /* The same component is mounted under both /admin/* and /sales/*, so the
       "Back" link has to land the user where they came from — bouncing a
       sales user to /admin/incentives would 403 them. */
    const backTo = loc.pathname.startsWith('/admin/')
        ? '/admin/incentives'
        : '/sales/incentives';

    const [inc, setInc] = useState(null);
    const [openSales, setOpenSales] = useState(false);
    const [openOps,   setOpenOps]   = useState(false);
    const [busy, setBusy] = useState(false);

    const load = () => api.get(`/client-incentives/${id}`).then(r => setInc(r.data));
    useEffect(() => { load(); }, [id]);

    if (!inc) return <div className="ag-card p-10 text-center text-sm text-ink-500">Loading…</div>;

    /* If the server redacted the response (BDA / BDE / Ops), show the slim
       personal-view page that only displays the user's own slice. Skip the
       admin-style header + tables that need pool/team data. */
    if (inc._redactedForViewer) {
        return <PersonalView inc={inc} backTo={backTo} />;
    }

    const isAdmin = ['admin', 'manager'].includes(me?.role);
    const isAssignedBdm = String(me?._id) === String(inc.salesTeam?.bdm?._id || inc.salesTeam?.bdm);
    const canEditSales  = (isAdmin || isAssignedBdm) && inc.status !== 'locked' && inc.status !== 'paid';
    const canEditOps    = isAdmin && inc.status !== 'locked' && inc.status !== 'paid';
    const canApprove    = isAdmin && (inc.status === 'awaiting_ops_split' || inc.status === 'awaiting_sales_split') && (inc.salesAllocations?.length > 0);
    const canUnlock     = isAdmin && inc.status === 'locked';
    const canMarkPaid   = isAdmin && inc.status === 'locked';

    const baseLabel = inc.poolBase === 'final_payable' ? 'Final payable' : 'Deal amount';
    const baseAmt   = inc.poolBase === 'final_payable' ? inc.finalPayableAmount : inc.dealAmount;

    const action = async (path, body) => {
        setBusy(true);
        try {
            await api.post(`/client-incentives/${id}/${path}`, body || {});
            toast.success('Done');
            await load();
        } catch {/* toasted */}
        finally { setBusy(false); }
    };

    const approve   = () => action('approve');
    const unlock    = () => {
        const reason = window.prompt('Why are you unlocking this incentive?');
        if (!reason || !reason.trim()) return;
        action('unlock', { reason: reason.trim() });
    };
    const markPaid  = () => action('paid');

    return (
        <div className="space-y-4 max-w-5xl">
            <Link to={backTo} className="text-xs text-ink-500 hover:text-brand-teal-700 inline-flex items-center gap-1">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to incentives
            </Link>

            <div className="ag-card p-5">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="text-xl font-extrabold">Deal closure incentive</h1>
                        <div className="text-xs text-ink-500 mt-1">
                            Created {inc.confirmedAt ? new Date(inc.confirmedAt).toLocaleString() : '—'} ·
                            office: <b>{inc.officeBranch || '—'}</b>
                        </div>
                    </div>
                    <StatusChip status={inc.status} />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 mt-4">
                    <KV k={baseLabel}       v={`₹ ${(baseAmt || 0).toLocaleString('en-IN')}`} />
                    <KV k="Pool"            v={`₹ ${(inc.poolAmount || 0).toLocaleString('en-IN')} (${inc.poolPercent}%)`} />
                    <KV k="Sales committed" v={`${inc.totalSalesPercent || 0}% · ₹ ${(inc.totalSalesAmount || 0).toLocaleString('en-IN')}`} />
                    <KV k="Ops committed"   v={`${inc.totalOpsPercent || 0}% · ₹ ${(inc.totalOpsAmount || 0).toLocaleString('en-IN')}`} />
                </div>

                {/* Action buttons */}
                <div className="flex flex-wrap gap-2 mt-4">
                    {canEditSales && (
                        <button className="ag-btn-primary text-xs" onClick={() => setOpenSales(true)}>
                            <FileText className="h-3.5 w-3.5" />
                            {inc.salesAllocations?.length ? 'Edit sales split' : 'Submit sales split'}
                        </button>
                    )}
                    {canEditOps && (
                        <button className="ag-btn-outline text-xs" onClick={() => setOpenOps(true)}>
                            <FileText className="h-3.5 w-3.5" />
                            {inc.opsAllocations?.length ? 'Edit ops split' : 'Add ops split'}
                        </button>
                    )}
                    {canApprove  && <button className="ag-btn-primary text-xs" disabled={busy} onClick={approve}><BadgeCheck className="h-3.5 w-3.5" /> Approve &amp; lock</button>}
                    {canUnlock   && <button className="ag-btn-outline text-xs" disabled={busy} onClick={unlock}><UnlockIcon className="h-3.5 w-3.5" /> Unlock</button>}
                    {canMarkPaid && <button className="ag-btn-primary text-xs" disabled={busy} onClick={markPaid}><IndianRupee className="h-3.5 w-3.5" /> Mark paid out</button>}
                </div>

                {!canEditSales && (inc.status === 'locked' || inc.status === 'paid') && (
                    <div className="mt-3 text-[11px] text-ink-500 inline-flex items-center gap-1">
                        <LockIcon className="h-3 w-3" /> Locked — edits require admin to Unlock first.
                    </div>
                )}
            </div>

            {/* Sales allocations */}
            <AllocTable
                title="Sales split"
                rows={(inc.salesAllocations || []).map(a => ({
                    name: a.user?.name || '—',
                    sub:  (a.role || '').toUpperCase(),
                    percent: a.percent, amount: a.amount,
                }))}
                emptyMsg="No sales split yet."
            />

            {/* Ops allocations */}
            <AllocTable
                title="Operations split"
                rows={(inc.opsAllocations || []).map(a => ({
                    name: a.user?.name || '—',
                    sub:  a.user?.salesDesignation === 'operations' ? 'Operations' : (a.user?.designation || ''),
                    percent: a.percent, amount: a.amount,
                    note: a.note,
                }))}
                emptyMsg="No ops split yet."
                showNote
            />

            {/* Clawback review (F2) — appears when a refund queued one or more proposals */}
            {(inc.clawbacks || []).length > 0 && (
                <div className="ag-card p-5 space-y-3">
                    <div className="flex items-center gap-2">
                        <RotateCcw className="h-4 w-4 text-amber-700" />
                        <h3 className="font-bold">Clawback queue</h3>
                        <span className="text-[11px] text-ink-500">
                            from refunds — review and finalise per beneficiary
                        </span>
                    </div>
                    {(inc.clawbacks || []).map((cb) => (
                        <ClawbackCard
                            key={cb._id}
                            cb={cb}
                            isAdmin={isAdmin}
                            incentiveId={inc._id}
                            onSaved={load}
                        />
                    ))}
                </div>
            )}

            {/* Audit log */}
            <div className="ag-card p-5">
                <div className="flex items-center gap-2 mb-2">
                    <History className="h-4 w-4 text-ink-500" />
                    <h3 className="font-bold">Audit log</h3>
                </div>
                <ul className="space-y-1.5">
                    {(inc.audit || []).slice().reverse().map((row, i) => (
                        <li key={i} className="text-xs flex items-baseline gap-2">
                            <span className="text-ink-500 tabular-nums whitespace-nowrap">{new Date(row.at).toLocaleString()}</span>
                            <span className="font-semibold uppercase tracking-wider text-[10px] bg-ink-100 text-ink-700 rounded px-1.5 py-0.5">{row.action}</span>
                            <span className="text-ink-700">{row.by?.name || '—'}</span>
                            {row.action === 'unlocked' && row.detail?.reason && (
                                <span className="text-ink-500 italic">· "{row.detail.reason}"</span>
                            )}
                        </li>
                    ))}
                    {(inc.audit || []).length === 0 && <li className="text-xs text-ink-500">No history yet.</li>}
                </ul>
            </div>

            {openSales && (
                <SalesSplitModal
                    inc={inc}
                    onClose={(saved) => {
                        setOpenSales(false);
                        if (saved) { toast.success('Sales split saved'); load(); }
                    }}
                />
            )}
            {openOps && (
                <OpsSplitModal
                    inc={inc}
                    onClose={(saved) => {
                        setOpenOps(false);
                        if (saved) { toast.success('Ops split saved'); load(); }
                    }}
                />
            )}
        </div>
    );
}

/* ─────────── Personal view (confidential — for BDA / BDE / Ops users) ───────────
 * Shown when the server returns a redacted payload (_redactedForViewer === true).
 * Strips every team-level number (pool, deal, totals, others' rows) and shows
 * only the viewer's own slice + the clawbacks that touch them. */
function PersonalView({ inc, backTo }) {
    const my = inc.myAllocation;

    return (
        <div className="space-y-4 max-w-2xl">
            <Link to={backTo} className="text-xs text-ink-500 hover:text-brand-teal-700 inline-flex items-center gap-1">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to incentives
            </Link>

            <div className="rounded-lg p-3 text-xs bg-ink-100 text-ink-700 ring-1 ring-ink-200 inline-flex items-start gap-2">
                <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-ink-500" />
                <div>
                    <b>Personal view.</b> Pool size, deal value, and team-mate amounts are confidential. Your
                    BDM and admin see the full split.
                </div>
            </div>

            <div className="ag-card p-5">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="text-xl font-extrabold">{inc.clientName || 'Client'}</h1>
                        <div className="text-xs text-ink-500 mt-1">
                            {inc.confirmedAt ? `Confirmed ${new Date(inc.confirmedAt).toLocaleString()}` : '—'}
                        </div>
                    </div>
                    <StatusChip status={inc.status} />
                </div>
            </div>

            {/* Hero — the only number that matters to this viewer */}
            <div className="ag-card p-6">
                <div className="text-[10px] uppercase tracking-wider font-bold text-ink-500">Your share</div>
                {my ? (
                    <>
                        <div className="text-3xl font-extrabold tabular-nums mt-1 text-brand-teal-700">
                            ₹ {(my.amount || 0).toLocaleString('en-IN')}
                        </div>
                        <div className="text-sm text-ink-500 mt-1">
                            {Number(my.percent || 0).toFixed(2)}%
                            {' '}as <b className="uppercase">{my.role}</b>
                        </div>
                    </>
                ) : (
                    <div className="text-sm text-ink-500 mt-2">
                        You don't have a slice on this incentive yet. Your BDM hasn't submitted the sales split,
                        or admin hasn't added you to the ops split. Once they do, your share will appear here.
                    </div>
                )}
            </div>

            {/* Clawback rows that touch this user — show the personal impact */}
            {(inc.clawbacks || []).length > 0 && (
                <div className="ag-card p-5 space-y-3">
                    <div className="flex items-center gap-2">
                        <RotateCcw className="h-4 w-4 text-amber-700" />
                        <h3 className="font-bold">Clawback on your share</h3>
                    </div>
                    {(inc.clawbacks || []).map(cb => {
                        const p = (cb.proposals || [])[0];
                        if (!p) return null;
                        const finalised = cb.status === 'finalised';
                        return (
                            <div key={cb._id} className={`rounded-lg p-3 text-sm ring-1
                                ${finalised ? 'bg-emerald-50/30 ring-emerald-200' : 'bg-amber-50/30 ring-amber-300'}`}>
                                <div className="flex items-baseline justify-between flex-wrap gap-2">
                                    <span className="text-xs text-ink-500">
                                        {new Date(cb.triggeredAt).toLocaleDateString()} · {finalised ? 'Finalised' : 'Pending admin review'}
                                    </span>
                                    <span className="text-[10px] uppercase tracking-wider text-ink-500">refund-driven</span>
                                </div>
                                <div className="mt-2 flex items-baseline gap-3">
                                    <span className="text-xs text-ink-500">Reduction from your incentive</span>
                                    <span className="font-bold tabular-nums text-rose-700">
                                        − ₹ {(finalised
                                            ? (p.finalClawback ?? p.proposedClawback)
                                            : p.proposedClawback
                                        ).toLocaleString('en-IN')}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Status timeline — small breadcrumb of events touching this user */}
            {(inc.audit || []).length > 0 && (
                <div className="ag-card p-5">
                    <div className="flex items-center gap-2 mb-2">
                        <History className="h-4 w-4 text-ink-500" />
                        <h3 className="font-bold">Activity</h3>
                    </div>
                    <ul className="space-y-1.5">
                        {(inc.audit || []).slice().reverse().map((row, i) => (
                            <li key={i} className="text-xs flex items-baseline gap-2">
                                <span className="text-ink-500 tabular-nums whitespace-nowrap">{new Date(row.at).toLocaleString()}</span>
                                <span className="font-semibold uppercase tracking-wider text-[10px] bg-ink-100 text-ink-700 rounded px-1.5 py-0.5">{row.action}</span>
                                <span className="text-ink-700">{row.by?.name || '—'}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

/* ─────────── Allocation table ─────────── */
function AllocTable({ title, rows, emptyMsg, showNote }) {
    return (
        <div className="ag-card p-0">
            <div className="px-5 py-3 border-b border-ink-100 flex items-center justify-between">
                <h3 className="font-bold">{title}</h3>
                {rows.length > 0 && (
                    <span className="text-xs text-ink-500">
                        Total {rows.reduce((s, r) => s + (r.percent || 0), 0).toFixed(2)}%
                    </span>
                )}
            </div>
            <div className="overflow-x-auto">
                <table className="ag-table">
                    <thead><tr><th>Person</th><th>Role</th>{showNote && <th>Note</th>}<th>%</th><th>Amount</th></tr></thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr><td colSpan={showNote ? 5 : 4} className="text-center text-ink-500 py-6">{emptyMsg}</td></tr>
                        )}
                        {rows.map((r, i) => (
                            <tr key={i}>
                                <td className="font-medium">{r.name}</td>
                                <td className="text-xs uppercase tracking-wider text-ink-500">{r.sub}</td>
                                {showNote && <td className="text-xs text-ink-500">{r.note || '—'}</td>}
                                <td className="tabular-nums">{(r.percent || 0).toFixed(2)}%</td>
                                <td className="tabular-nums">₹ {(r.amount || 0).toLocaleString('en-IN')}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/* ─────────── Sales split modal ─────────── */
function SalesSplitModal({ inc, onClose }) {
    /* Seed from current allocations so admin can revise. */
    const seed = (role) => {
        const a = (inc.salesAllocations || []).find(x => x.role === role);
        return a ? a.percent : '';
    };
    const [vals, setVals] = useState({ bda: seed('bda'), bde: seed('bde'), bdm: seed('bdm') });
    const [busy, setBusy] = useState(false);

    /* Read the ops floor from the snapshot on the incentive doc — locked in
       at confirm time. No /api/settings call needed (BDMs can't read it). */
    const opsMinPercent = Number(inc.opsMinPercent) || 0;

    const sum  = ['bda','bde','bdm'].reduce((s, k) => s + (Number(vals[k]) || 0), 0);
    const maxAllowed = Math.max(0, (inc.poolPercent || 0) - opsMinPercent);
    const overBy = Math.max(0, sum - maxAllowed);

    const submit = async () => {
        if (overBy > 0) return toast.error(`Total ${sum.toFixed(2)}% exceeds the ${maxAllowed.toFixed(2)}% max.`);
        setBusy(true);
        try {
            const allocations = ['bda','bde','bdm'].map(role => ({
                role,
                user: inc.salesTeam?.[role]?._id || inc.salesTeam?.[role],
                percent: Number(vals[role]) || 0,
            }));
            await api.post(`/client-incentives/${inc._id}/sales-split`, { allocations });
            onClose(true);
        } catch {/* toasted */}
        finally { setBusy(false); }
    };

    const Row = ({ role }) => {
        const u = inc.salesTeam?.[role];
        return (
            <div className="flex items-center gap-3 py-2 border-b border-ink-100 last:border-0">
                <span className="text-[10px] font-bold rounded-full bg-brand-teal-50 text-brand-teal-700 px-2 py-0.5 uppercase">{role}</span>
                <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{u?.name || <span className="text-rose-600">not assigned</span>}</div>
                    <div className="text-[11px] text-ink-500 truncate">{u?.email}</div>
                </div>
                <input
                    type="number" step="0.01" min="0" max="100"
                    className="ag-input w-28 text-right tabular-nums"
                    value={vals[role]}
                    onChange={e => setVals({ ...vals, [role]: e.target.value })}
                    disabled={!u}
                />
                <span className="text-xs text-ink-500 w-4">%</span>
            </div>
        );
    };

    return (
        <ModalShell title="Sales split (BDA / BDE / BDM)" onClose={() => onClose(false)} width="max-w-lg">
            <p className="text-xs text-ink-500">
                Set each person's percent <b>of the deal amount</b> (not of the pool). For example, 0.40
                means 0.40% of the closed-deal value goes to that person.
            </p>

            <div>
                <Row role="bda" />
                <Row role="bde" />
                <Row role="bdm" />
            </div>

            <div className={`rounded-lg p-3 text-sm flex items-center justify-between
                ${overBy > 0 ? 'bg-rose-50 text-rose-700 ring-1 ring-rose-200'
                             : 'bg-brand-teal-50 text-brand-teal-700'}`}>
                <span>Total committed</span>
                <span className="font-bold tabular-nums">{sum.toFixed(2)}% / max {maxAllowed.toFixed(2)}%</span>
            </div>
            {overBy > 0 && (
                <p className="text-[11px] text-rose-700 inline-flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> Reduce by {overBy.toFixed(2)}% — the pool is {inc.poolPercent}%
                    {opsMinPercent > 0 && <> with a {opsMinPercent}% floor reserved for Operations</>}.
                </p>
            )}

            <div className="flex justify-end gap-2 pt-2">
                <button className="ag-btn-ghost" onClick={() => onClose(false)}>Cancel</button>
                <button className="ag-btn-primary" disabled={busy || overBy > 0} onClick={submit}>
                    {busy ? 'Saving…' : 'Save sales split'}
                </button>
            </div>
        </ModalShell>
    );
}

/* ─────────── Ops split modal ─────────── */
function OpsSplitModal({ inc, onClose }) {
    const [people, setPeople] = useState(null);
    const [rows, setRows]     = useState((inc.opsAllocations || []).map(a => ({
        user:    a.user?._id || a.user,
        percent: a.percent,
        note:    a.note || '',
    })));
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get('/users/picker', { params: { salesDesignation: 'operations' } })
           .then(r => setPeople(r.data.items || []));
    }, []);

    const salesSum = (inc.salesAllocations || []).reduce((s, a) => s + (a.percent || 0), 0);
    const opsSum   = rows.reduce((s, r) => s + (Number(r.percent) || 0), 0);
    const remaining = Math.max(0, (inc.poolPercent || 0) - salesSum - opsSum);
    const over      = Math.max(0, salesSum + opsSum - inc.poolPercent);

    const addRow  = () => setRows([...rows, { user: '', percent: '', note: '' }]);
    const rmRow   = (i) => setRows(rows.filter((_, x) => x !== i));
    const setRow  = (i, patch) => setRows(rows.map((r, n) => n === i ? { ...r, ...patch } : r));

    const submit = async () => {
        if (over > 0) return toast.error(`Sales + ops exceeds pool by ${over.toFixed(2)}%`);
        const cleaned = rows
            .filter(r => r.user && Number(r.percent) > 0)
            .map(r => ({ user: r.user, percent: Number(r.percent), note: r.note || '' }));
        setBusy(true);
        try {
            await api.post(`/client-incentives/${inc._id}/ops-split`, { allocations: cleaned });
            onClose(true);
        } catch {/* toasted */}
        finally { setBusy(false); }
    };

    return (
        <ModalShell title="Operations split" onClose={() => onClose(false)} width="max-w-2xl">
            <p className="text-xs text-ink-500">
                Pool {inc.poolPercent}% · sales already committed <b>{salesSum.toFixed(2)}%</b>.
                Remaining available: <b>{remaining.toFixed(2)}%</b>.
            </p>

            <div className="space-y-2">
                {rows.map((r, i) => (
                    <div key={i} className="flex items-center gap-2 py-2 border-b border-ink-100 last:border-0">
                        <select
                            className="ag-input flex-1"
                            value={r.user}
                            onChange={e => setRow(i, { user: e.target.value })}
                        >
                            <option value="">— Ops user —</option>
                            {(people || []).map(p => (
                                <option key={p._id} value={p._id}>
                                    {p.name} · {p.email}{p.officeBranch ? ` · ${p.officeBranch}` : ''}
                                </option>
                            ))}
                        </select>
                        <input
                            type="number" step="0.01" min="0" max="100"
                            className="ag-input w-24 text-right tabular-nums"
                            placeholder="%"
                            value={r.percent}
                            onChange={e => setRow(i, { percent: e.target.value })}
                        />
                        <input
                            className="ag-input flex-1"
                            placeholder="Note (optional)"
                            value={r.note}
                            onChange={e => setRow(i, { note: e.target.value })}
                        />
                        <button onClick={() => rmRow(i)} className="text-ink-500 hover:text-brand-red shrink-0">
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </div>
                ))}
                <button onClick={addRow} className="ag-btn-ghost text-xs"><Plus className="h-3.5 w-3.5" /> Add ops user</button>
            </div>

            {people && people.length === 0 && (
                <div className="text-[11px] text-amber-700 bg-amber-50 ring-1 ring-amber-200 rounded p-2 inline-flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5" /> No users have salesDesignation = "operations" yet.
                    Set it on Admin → Users first.
                </div>
            )}

            <div className={`rounded-lg p-3 text-sm flex items-center justify-between
                ${over > 0 ? 'bg-rose-50 text-rose-700 ring-1 ring-rose-200' : 'bg-brand-teal-50 text-brand-teal-700'}`}>
                <span>Sales {salesSum.toFixed(2)}% + Ops {opsSum.toFixed(2)}%</span>
                <span className="font-bold tabular-nums">= {(salesSum + opsSum).toFixed(2)}% / pool {inc.poolPercent}%</span>
            </div>

            <div className="flex justify-end gap-2 pt-2">
                <button className="ag-btn-ghost" onClick={() => onClose(false)}>Cancel</button>
                <button className="ag-btn-primary" disabled={busy || over > 0} onClick={submit}>
                    {busy ? 'Saving…' : 'Save ops split'}
                </button>
            </div>
        </ModalShell>
    );
}

/* ─────────── Clawback card (F2) ───────────
 * Inline editor for finalising the proportional clawback. Admin only —
 * sales user sees the same card in read-only mode (no edit affordances).
 * Beneficiary rows show their original incentive amount, the proposed
 * clawback (refundFraction × original), and an editable "final" cell
 * defaulting to the proposed value. Save commits the final amounts. */
function ClawbackCard({ cb, isAdmin, incentiveId, onSaved }) {
    const [finals, setFinals] = useState(() => {
        const seed = {};
        for (const p of (cb.proposals || [])) {
            seed[String(p.user?._id || p.user)] = p.finalClawback != null
                ? p.finalClawback
                : p.proposedClawback;
        }
        return seed;
    });
    const [note, setNote] = useState(cb.note || '');
    const [busy, setBusy] = useState(false);

    const locked = cb.status !== 'pending_admin_review';
    const totalProposed = (cb.proposals || []).reduce((s, p) => s + (p.proposedClawback || 0), 0);
    const totalFinal    = Object.values(finals).reduce((s, v) => s + (Number(v) || 0), 0);

    const save = async () => {
        if (!isAdmin || locked) return;
        setBusy(true);
        try {
            const proposals = (cb.proposals || []).map(p => ({
                user:          String(p.user?._id || p.user),
                finalClawback: Math.max(0, Math.min(p.originalAmount, Number(finals[String(p.user?._id || p.user)]) || 0)),
            }));
            await api.post(`/refunds/clawback/${incentiveId}/${cb._id}/finalise`, { proposals, note: note.trim() });
            toast.success('Clawback finalised');
            onSaved && onSaved();
        } catch {/* toasted */}
        finally { setBusy(false); }
    };

    return (
        <div className={`rounded-xl ring-1 p-4 ${locked ? 'ring-emerald-200 bg-emerald-50/30' : 'ring-amber-300 bg-amber-50/30'}`}>
            <div className="flex items-baseline justify-between flex-wrap gap-2">
                <div className="text-sm">
                    <b>Refund ₹{(cb.refundAmount || 0).toLocaleString('en-IN')}</b>
                    <span className="text-ink-500 ml-2">
                        ({(cb.refundFraction * 100).toFixed(2)}% of deal · triggered {new Date(cb.triggeredAt).toLocaleDateString()})
                    </span>
                </div>
                <span className={`ag-chip ${locked ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                    {locked ? cb.status : 'awaiting admin'}
                </span>
            </div>

            <div className="overflow-x-auto mt-3">
                <table className="w-full text-sm">
                    <thead><tr className="text-[10px] uppercase tracking-wider text-ink-500">
                        <th className="text-left py-1">Beneficiary</th>
                        <th className="text-left py-1">Role</th>
                        <th className="text-right py-1">Original</th>
                        <th className="text-right py-1">Proposed clawback</th>
                        <th className="text-right py-1">Final</th>
                    </tr></thead>
                    <tbody>
                        {(cb.proposals || []).map(p => {
                            const id = String(p.user?._id || p.user);
                            return (
                                <tr key={id} className="border-t border-ink-100">
                                    <td className="py-1.5">{p.user?.name || '—'}</td>
                                    <td className="py-1.5 text-xs uppercase tracking-wider text-ink-500">{p.role || '—'}</td>
                                    <td className="py-1.5 text-right tabular-nums">₹ {(p.originalAmount || 0).toLocaleString('en-IN')}</td>
                                    <td className="py-1.5 text-right tabular-nums text-amber-700">₹ {(p.proposedClawback || 0).toLocaleString('en-IN')}</td>
                                    <td className="py-1.5 text-right">
                                        {locked
                                            ? <span className="tabular-nums font-semibold">₹ {(p.finalClawback || 0).toLocaleString('en-IN')}</span>
                                            : isAdmin
                                                ? <input
                                                    type="number" step="1" min="0" max={p.originalAmount}
                                                    className="ag-input w-28 text-right tabular-nums"
                                                    value={finals[id] ?? ''}
                                                    onChange={e => setFinals(f => ({ ...f, [id]: e.target.value }))}
                                                />
                                                : <span className="tabular-nums text-ink-500">pending</span>}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="rounded bg-white/60 ring-1 ring-ink-100 p-2">
                    <div className="text-[10px] uppercase tracking-wider text-ink-500">Total proposed</div>
                    <div className="font-bold tabular-nums">₹ {totalProposed.toLocaleString('en-IN')}</div>
                </div>
                <div className="rounded bg-white/60 ring-1 ring-ink-100 p-2">
                    <div className="text-[10px] uppercase tracking-wider text-ink-500">Total final</div>
                    <div className="font-bold tabular-nums">₹ {Math.round(totalFinal).toLocaleString('en-IN')}</div>
                </div>
            </div>

            {!locked && isAdmin && (
                <>
                    <textarea
                        className="ag-input min-h-[60px] mt-3"
                        placeholder="Admin note (optional)"
                        value={note}
                        onChange={e => setNote(e.target.value)}
                    />
                    <div className="flex justify-end mt-2">
                        <button className="ag-btn-primary text-xs" disabled={busy} onClick={save}>
                            {busy ? 'Saving…' : 'Finalise clawback'}
                        </button>
                    </div>
                </>
            )}
            {locked && cb.note && (
                <div className="mt-2 text-[11px] italic text-ink-500">"{cb.note}"</div>
            )}
        </div>
    );
}
